import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../services/supabaseClient'
import { useAuth } from '../hooks/useAuth'

interface TeamMember {
  user_id: string
  email: string
  full_name: string | null
  role: string
  joined_at: string
  org_count: number
  org_names: string[]
  org_access?: string[]
  available_orgs?: Array<{ id: string; name: string; is_sandbox: boolean }>
}

interface Invitation {
  id: string
  email: string
  role: string
  status: string
  expires_at: string
  created_at: string
  inviter: { email: string }
}

interface TeamManagementProps {
  accountId: string
}

export default function TeamManagement({ accountId }: TeamManagementProps) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [members, setMembers] = useState<TeamMember[]>([])
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [loading, setLoading] = useState(true)
  const [userRole, setUserRole] = useState<string>('')
  const [canManageTeam, setCanManageTeam] = useState(false)
  const [teamLimits, setTeamLimits] = useState<{
    currentUsers: number
    maxUsers: number | null
    atUserLimit: boolean
    enforceUserLimit: boolean
    subscriptionPlan: string
    availableSlots: number | null
    canInviteMore: boolean
  } | null>(null)
  
  // Helper function for authenticated API calls
  const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) throw new Error('No session')
    
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      }
    })
  }
  
  // Invite form state
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('Editor')
  const [inviteMessage, setInviteMessage] = useState('')
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState('')
  const [inviteSuccess, setInviteSuccess] = useState('')
  
  // Org access modal state
  const [showOrgAccessModal, setShowOrgAccessModal] = useState(false)
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null)
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([])
  const [updatingOrgAccess, setUpdatingOrgAccess] = useState(false)

  useEffect(() => {
    fetchTeamData()
  }, [accountId])

  const fetchTeamData = async () => {
    try {
      console.log('Fetching team data for accountId:', accountId)
      
      if (!accountId) {
        console.error('No accountId provided to TeamManagement component')
        return
      }
      
      // Get user session for auth
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        console.error('No session found')
        return
      }
      
      // Fetch team members with org access info
      const membersResponse = await fetch(`/api/team/members?accountId=${accountId}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })
      const membersData = await membersResponse.json()
      
      console.log('Members API response:', membersResponse.status, membersData)
      
      if (membersResponse.ok) {
        // Sort members: Super-Admin first, then alphabetically by email
        const sortedMembers = (membersData.members || []).sort((a: TeamMember, b: TeamMember) => {
          // Super-Admins come first
          if (a.role === 'Super-Admin' && b.role !== 'Super-Admin') return -1
          if (b.role === 'Super-Admin' && a.role !== 'Super-Admin') return 1

          // Then sort alphabetically by email
          return (a.email || '').localeCompare(b.email || '')
        })

        setMembers(sortedMembers)
        setUserRole(membersData.userRole || '')
        setCanManageTeam(membersData.canManageTeam || false)
        console.log('User role:', membersData.userRole, 'Can manage team:', membersData.canManageTeam)
      } else {
        console.error('Error from members API:', membersData.error)
      }
      
      // Fetch invitations if user can manage team
      if (membersData.canManageTeam) {
        const invitationsResponse = await fetch(`/api/invitations/list?accountId=${accountId}`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        })
        const invitationsData = await invitationsResponse.json()
        
        if (invitationsResponse.ok) {
          setInvitations(invitationsData.invitations || [])
        }
      }

      // Fetch team limits
      const limitsResponse = await fetch(`/api/team/check-user-limit?accountId=${accountId}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })
      const limitsData = await limitsResponse.json()
      
      if (limitsResponse.ok) {
        setTeamLimits(limitsData)
      }
    } catch (error) {
      console.error('Error fetching team data:', error)
    } finally {
      setLoading(false)
    }
  }

  const sendInvitation = async (e: React.FormEvent) => {
    e.preventDefault()
    setInviting(true)
    setInviteError('')
    setInviteSuccess('')
    
    try {
      const response = await fetchWithAuth('/api/invitations/create', {
        method: 'POST',
        body: JSON.stringify({
          email: inviteEmail,
          role: inviteRole,
          accountId,
          customMessage: inviteMessage
        })
      })
      
      const data = await response.json()
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send invitation')
      }
      
      setInviteSuccess(data.message)
      
      // Show the invitation URL as fallback if email fails
      if (data.invitation?.url && data.message.includes('share')) {
        alert(`Invitation created! Share this link with ${inviteEmail}:\n\n${data.invitation.url}\n\nThe link expires in 7 days.`)
      }
      
      setInviteEmail('')
      setInviteMessage('')
      setInviteRole('Editor')
      
      // Refresh data
      fetchTeamData()
      
      // Hide form after success
      setTimeout(() => {
        setShowInviteForm(false)
        setInviteSuccess('')
      }, 3000)
    } catch (error: any) {
      // Check if it's a user limit error
      if (error.message.includes('limit')) {
        const response = await fetchWithAuth('/api/team/check-user-limit?accountId=' + accountId)
        const limitsData = await response.json()
        if (response.ok) {
          setTeamLimits(limitsData)
        }
      }
      setInviteError(error.message)
    } finally {
      setInviting(false)
    }
  }

  const cancelInvitation = async (invitationId: string) => {
    if (!confirm('Are you sure you want to cancel this invitation?')) return
    
    try {
      const response = await fetchWithAuth(`/api/invitations/cancel?id=${invitationId}`, {
        method: 'DELETE'
      })
      
      if (response.ok) {
        fetchTeamData()
      } else {
        const errorData = await response.json()
        console.error('Error cancelling invitation:', errorData)
        alert(`Failed to cancel invitation: ${errorData.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error cancelling invitation:', error)
      alert('Failed to cancel invitation. Please try again.')
    }
  }

  const updateMemberRole = async (userId: string, newRole: string) => {
    try {
      const response = await fetchWithAuth('/api/team/members', {
        method: 'PATCH',
        body: JSON.stringify({ userId, accountId, newRole })
      })
      
      if (response.ok) {
        fetchTeamData()
      }
    } catch (error) {
      console.error('Error updating role:', error)
    }
  }

  const removeMember = async (userId: string) => {
    if (!confirm('Are you sure you want to remove this team member?')) return
    
    try {
      const response = await fetchWithAuth(`/api/team/members?userId=${userId}&accountId=${accountId}`, {
        method: 'DELETE'
      })
      
      if (response.ok) {
        fetchTeamData()
      }
    } catch (error) {
      console.error('Error removing member:', error)
    }
  }

  const openOrgAccessModal = (member: TeamMember) => {
    setSelectedMember(member)
    setSelectedOrgs(member.org_access || [])
    setShowOrgAccessModal(true)
  }

  const updateOrgAccess = async () => {
    if (!selectedMember) return
    
    setUpdatingOrgAccess(true)
    
    try {
      const response = await fetchWithAuth('/api/team/org-access-vercel', {
        method: 'PUT',
        body: JSON.stringify({
          userId: selectedMember.user_id,
          orgIds: selectedOrgs,
          accountId
        })
      })
      
      if (response.ok) {
        setShowOrgAccessModal(false)
        fetchTeamData()
      }
    } catch (error) {
      console.error('Error updating org access:', error)
    } finally {
      setUpdatingOrgAccess(false)
    }
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'Super-Admin': return 'bg-purple-100 text-purple-800'
      case 'Admin': return 'bg-blue-100 text-blue-800'
      case 'Editor': return 'bg-green-100 text-green-800'
      case 'Viewer': return 'bg-yellow-100 text-yellow-800'
      case 'Guest': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return <div className="animate-pulse">Loading team data...</div>
  }

  // Debug info
  if (!accountId) {
    return <div className="text-red-600">Error: No account ID provided</div>
  }

  return (
    <div className="space-y-6">
      {/* Team Members Section */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Team Members</h3>
            {teamLimits && (
              <p className="text-sm text-gray-500 mt-1">
                {teamLimits.currentUsers} of {teamLimits.maxUsers || 'unlimited'} users
                {teamLimits.pendingInvitations > 0 && (
                  <span className="text-amber-600">
                    {' '}• {teamLimits.pendingInvitations} pending invitation{teamLimits.pendingInvitations > 1 ? 's' : ''}
                  </span>
                )}
              </p>
            )}
          </div>
          {canManageTeam && (
            <button
              onClick={() => setShowInviteForm(!showInviteForm)}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Invite User
            </button>
          )}
          {canManageTeam && teamLimits?.upgradeRequired && (
            <a
              href="/plans"
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200"
            >
              <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              Upgrade to Add More Users
            </a>
          )}
        </div>

        {/* Invite Form */}
        {showInviteForm && (
          <div className="mb-6 bg-gray-50 p-4 rounded-lg">
            <form onSubmit={sendInvitation} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="invite-email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="user@example.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="invite-role" className="block text-sm font-medium text-gray-700">
                    Role
                  </label>
                  <select
                    id="invite-role"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="Guest">Guest</option>
                    <option value="Viewer">Viewer</option>
                    <option value="Editor">Editor</option>
                    {['Admin', 'Super-Admin'].includes(userRole) && (
                      <option value="Admin">Admin</option>
                    )}
                    {userRole === 'Super-Admin' && (
                      <option value="Super-Admin">Super Admin</option>
                    )}
                  </select>
                </div>
              </div>
              
              <div>
                <label htmlFor="invite-message" className="block text-sm font-medium text-gray-700">
                  Personal Message (Optional)
                </label>
                <textarea
                  id="invite-message"
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  rows={2}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Add a personal message to the invitation..."
                />
              </div>
              
              {inviteError && (
                <div className="text-sm text-red-600">{inviteError}</div>
              )}
              
              {inviteSuccess && (
                <div className="text-sm text-green-600">{inviteSuccess}</div>
              )}
              
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={inviting}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                    inviting ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                >
                  {inviting ? 'Sending...' : 'Send Invitation'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteForm(false)
                    setInviteEmail('')
                    setInviteMessage('')
                    setInviteError('')
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Members Table */}
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Org Access
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                {canManageTeam && (
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {members.map((member) => (
                <tr key={member.user_id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{member.email}</div>
                    {member.full_name && (
                      <div className="text-sm text-gray-500">{member.full_name}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {canManageTeam && member.user_id !== user?.id ? (
                      <select
                        value={member.role}
                        onChange={(e) => updateMemberRole(member.user_id, e.target.value)}
                        className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="Guest">Guest</option>
                        <option value="Viewer">Viewer</option>
                        <option value="Editor">Editor</option>
                        {['Admin', 'Super-Admin'].includes(userRole) && (
                          <option value="Admin">Admin</option>
                        )}
                        {userRole === 'Super-Admin' && (
                          <option value="Super-Admin">Super Admin</option>
                        )}
                      </select>
                    ) : (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(member.role)}`}>
                        {member.role.replace('_', ' ')}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {member.role === 'Super-Admin' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        All Orgs
                      </span>
                    ) : canManageTeam ? (
                      <button
                        onClick={() => openOrgAccessModal(member)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        {member.org_count} org{member.org_count !== 1 ? 's' : ''} →
                      </button>
                    ) : (
                      <span>{member.org_count} org{member.org_count !== 1 ? 's' : ''}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(member.joined_at).toLocaleDateString()}
                  </td>
                  {canManageTeam && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {member.user_id !== user?.id && (
                        <button
                          onClick={() => removeMember(member.user_id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pending Invitations */}
      {canManageTeam && invitations.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Pending Invitations</h3>
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {invitations.map((invitation) => (
                <li key={invitation.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{invitation.email}</p>
                      <p className="text-sm text-gray-500">
                        Role: {invitation.role} • Invited by {invitation.inviter.email} • 
                        Expires {new Date(invitation.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => cancelInvitation(invitation.id)}
                      className="text-sm text-red-600 hover:text-red-900"
                    >
                      Cancel
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Org Access Modal */}
      {showOrgAccessModal && selectedMember && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Manage Org Access for {selectedMember.email}
            </h3>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {!selectedMember.available_orgs || selectedMember.available_orgs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <p className="mt-2 text-sm">No Salesforce orgs connected yet</p>
                  <p className="mt-1 text-xs">Connect a Salesforce org first to manage access</p>
                </div>
              ) : (
                selectedMember.available_orgs.map((org) => (
                  <label key={org.id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                    <input
                      type="checkbox"
                      checked={selectedOrgs.includes(org.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedOrgs([...selectedOrgs, org.id])
                        } else {
                          setSelectedOrgs(selectedOrgs.filter(id => id !== org.id))
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-3 text-sm text-gray-900">
                      {org.name}
                      <span className="text-gray-500 text-xs ml-1">
                        ({org.is_sandbox ? 'Sandbox' : 'Production'})
                      </span>
                    </span>
                  </label>
                ))
              )}
            </div>
            
            <div className="mt-6 flex space-x-3">
              {!selectedMember.available_orgs || selectedMember.available_orgs.length === 0 ? (
                <>
                  <button
                    onClick={() => {
                      setShowOrgAccessModal(false)
                      navigate('/orgs')
                    }}
                    className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Connect an Org
                  </button>
                  <button
                    onClick={() => {
                      setShowOrgAccessModal(false)
                      setSelectedMember(null)
                      setSelectedOrgs([])
                    }}
                    className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Close
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={updateOrgAccess}
                    disabled={updatingOrgAccess}
                    className={`flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                      updatingOrgAccess ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                  >
                    {updatingOrgAccess ? 'Updating...' : 'Update Access'}
                  </button>
                  <button
                    onClick={() => {
                      setShowOrgAccessModal(false)
                      setSelectedMember(null)
                      setSelectedOrgs([])
                    }}
                    className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}